import {
	DEFAULT_BUDGETS,
	type MpCheckpoint,
	type MpLeague,
} from "../../common/multiplayerConfig.ts";
import { getClient } from "./index.ts";

export const setReady = async (
	leagueId: string,
	ready: boolean,
): Promise<
	| { status: "ok"; ready: number; total: number }
	| { status: "sim_open" }
	| { status: "sim_in_progress" }
	// Legacy Phase 3 servers advanced directly on all-ready
	| { status: "advanced"; from: MpCheckpoint; to: MpCheckpoint }
> => {
	const { data, error } = await getClient().rpc("set_ready", {
		p_league_id: leagueId,
		p_ready: ready,
	});
	if (error) {
		throw new Error(error.message);
	}
	return data;
};

export const budgetForCheckpoint = (
	league: MpLeague,
	checkpoint: MpCheckpoint,
) => {
	return league.settings.budgets?.[checkpoint] ?? DEFAULT_BUDGETS[checkpoint];
};

// How many trades my team has made since the current checkpoint began.
// Mirrors the server's mp_trades_used.
export const tradesUsed = async (league: MpLeague, tid: number) => {
	// Must match the SQL mp_trades_used (the enforcement path): count this
	// team's trades since the checkpoint, minus any that were undone, so an
	// undone accidental trade also refunds the displayed budget.
	const { data, error } = await getClient()
		.from("league_events")
		.select("type, payload")
		.eq("league_id", league.id)
		.in("type", ["trade", "trade_undo"])
		.gt("seq", league.checkpoint_seq);
	if (error) {
		throw new Error(error.message);
	}
	const rows = data ?? [];
	const involvesTid = (row: any) => row.payload?.tids?.includes(tid);
	const trades = rows.filter((r: any) => r.type === "trade" && involvesTid(r));
	const undos = rows.filter(
		(r: any) => r.type === "trade_undo" && involvesTid(r),
	);
	return Math.max(0, trades.length - undos.length);
};
